import { Grid, LoadingDots } from '@flos/react-ui';
import { useRecoilState, useRecoilValue } from 'recoil';
import { missingInformationAtom } from '../../../recoil/atoms/missingInformationAtom';
import {
    areAnyChildren40DaysFrom18,
    areAnyChildrenAbove18,
    areAnyYoungAccident40DaysFrom28OrAbove,
    isMissingInformationDTOValidated
} from '../configs/ValidateMissingDTOConfig';
import AccordionWizardTemplate from '../components/AccordionWizardTemplate';
import PetMissingInformation from '../components/missingInformationCoverables/PetMissingInformation';
import { useUpdateMissingInformation } from '../../../recoil/hooks/useUpdateMissingInformation';
import { Suspense, useState } from 'react';
import SingleTripMissingInformation from '../components/missingInformationCoverables/SingleTripMissingInformation';
import ChildAccidentMissingInformation from '../components/missingInformationCoverables/ChildAccidentMissingInformation';
import ContentMissingInformation from '../components/missingInformationCoverables/ContentMissingInformation';
import CaravanMissingInformation from '../components/missingInformationCoverables/CaravanMissingInformation';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import { useHippoText } from '../../../hooks/useHippoText';
import { allCoverablesAtom } from '../../../recoil/atoms/allCoverablesAtom';
import {
    ACCORDION_STEPS,
    containsOnlyAccidentSpinOff,
    isTheAccordionTheLastStep
} from '../configs/CheckoutAccordionConfig';
import OtherPersonMissingInformation from '../components/missingInformationCoverables/OtherPersonMissingInformation';
import CarMissingInformation from '../components/missingInformationCoverables/CarMissingInformation';
import HouseMissingInformation from '../components/missingInformationCoverables/houseMissingInformation/HouseMissingInformation';
import { track, trackPurchase } from '../../../utils/trackingUtil';
import type { MissingInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/missingInformation';
import { externalCasesAtom } from '../../../recoil/atoms/externalCasesAtom';
import { jobInfoAtom } from '../../../recoil/atoms/jobInfoAtom';

import { useIssueInsurance } from '../../../hooks/useIssueInsurance';
import { allCoverablesToCoverableDTOArray } from '../../../utils/coverablesUtil';
import { containsAddCoverageChangeSelector } from '../../../recoil/selectors/containsAddCoverageSelector';
import { notUpdatedCoverablesAtom } from '../../../recoil/atoms/notUpdatedCoverablesAtom';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';

export const MissingInformationStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const missingInformation = useRecoilValue(missingInformationAtom);
    const [notUpdatedCoverables, setNotUpdatedCoverables] =
        useRecoilState(notUpdatedCoverablesAtom);
    const { isWaiting, updateMissingInformation } = useUpdateMissingInformation(goNext);
    const getHippoText = useHippoText();
    const { jobNumber, isPolicyChange } = useRecoilValue(jobInfoAtom);
    const { isCVR } = useRecoilValue(accountHolderAtom);
    const [showCallMeModal, setShowCallMeModal] = useState(false);
    const coverables = useRecoilValue(allCoverablesAtom);
    const externalCasesData = useRecoilValue(externalCasesAtom);
    const containsAddCoverageChange = useRecoilValue(containsAddCoverageChangeSelector);
    const { issueInsurance } = useIssueInsurance();

    const getErrorText = (): string | undefined => {
        const missingChildData = missingInformation.childAccident ?? [];
        const missingOtherPersonData = missingInformation.accidentOtherPerson ?? [];

        if (areAnyChildrenAbove18(missingChildData)) {
            return getHippoText('DSS-ADDITIONAL-INFORMATION-CHILD-ABOVE-EIGHTTEEN');
        } else if (areAnyChildren40DaysFrom18(missingChildData)) {
            return getHippoText('DSS-ADDITIONAL-INFORMATION-CHILD-SOON-EIGHTTEEN');
        } else if (areAnyYoungAccident40DaysFrom28OrAbove(missingOtherPersonData)) {
            return getHippoText('DSS-ADDITIONAL-INFORMATION-YOUNG-ABOVE-TWENTYEIGHT');
        }
    };

    const onGoNext = (): void => {
        if (getErrorText()) {
            setShowCallMeModal(true);
        } else {
            void updateMissingInfo();
        }
    };

    const updateMissingInfo = async (): Promise<void> => {
        const filledMissingInformation: Array<
            Record<string, MissingInformationDTO[keyof MissingInformationDTO]>
        > = Object.entries(missingInformation).flatMap(([key, value]) =>
            Array.isArray(value)
                ? value.map((item, index) => {
                      const uniqueKey = `${key}-${index + 1}`;
                      return { [uniqueKey]: item };
                  })
                : []
        );
        track('Missing information step', filledMissingInformation);
        await updateMissingInformation(missingInformation);
        if (!notUpdatedCoverables) setNotUpdatedCoverables(coverables);
        if (
            isTheAccordionTheLastStep(
                coverables,
                externalCasesData,
                isPolicyChange,
                ACCORDION_STEPS.MISSING_INFORMATION,
                isCVR,
                containsAddCoverageChange,
                notUpdatedCoverables
            )
        ) {
            await issueInsurance();
            trackPurchase(allCoverablesToCoverableDTOArray(coverables), jobNumber);
        }
    };

    if (missingInformation === undefined) return <></>;

    return (
        <AccordionWizardTemplate
            goNext={onGoNext}
            disableNext={!isMissingInformationDTOValidated(missingInformation, coverables)}
            isNextBtnLoading={isWaiting}
            continueButtonText={
                containsOnlyAccidentSpinOff(coverables) ? 'Færdiggør bestillingen' : ''
            }
        >
            <Suspense fallback={<LoadingDots />}>
                <Grid gap={6}>
                    <PetMissingInformation />
                    <SingleTripMissingInformation />
                    <ChildAccidentMissingInformation />
                    <ContentMissingInformation />
                    <OtherPersonMissingInformation />
                    <CarMissingInformation />
                    <HouseMissingInformation />
                    <CaravanMissingInformation />
                </Grid>
            </Suspense>
            {showCallMeModal && (
                <CallMeModal
                    activityCode="dss_add_coverage_phone_req"
                    showModal={showCallMeModal}
                    description={getErrorText()}
                    closeModal={() => {
                        setShowCallMeModal(false);
                    }}
                />
            )}
        </AccordionWizardTemplate>
    );
};
